// Weekly-pattern availability shared between staff (users) and clients.
// Mirrored on the server in `server/src/availabilityValidation.js`.
//
// Semantics:
// - A record with NO days set at all (empty object `{}`) is treated as
//   "not configured" and suppresses scheduling warnings entirely.
// - Once any day is set, days without blocks are "unavailable that day"
//   and will trigger warnings if a session is booked then.
// - Warnings are informational only (never block session creation),
//   consistent with the existing conflict-warning posture.

export type DayOfWeek = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export const DAYS_OF_WEEK: DayOfWeek[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const DAY_LABELS: Record<DayOfWeek, string> = {
    mon: 'Monday',
    tue: 'Tuesday',
    wed: 'Wednesday',
    thu: 'Thursday',
    fri: 'Friday',
    sat: 'Saturday',
    sun: 'Sunday',
};

/** HH:mm in 24h clock. */
export interface TimeBlock {
    start: string;
    end: string;
}

export type Availability = Partial<Record<DayOfWeek, TimeBlock[]>>;

/** JavaScript Date.getDay() is 0=Sun..6=Sat. Map to our keys. */
const JS_DAY_TO_KEY: DayOfWeek[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export function dayKeyFromDate(date: Date): DayOfWeek {
    return JS_DAY_TO_KEY[date.getDay()];
}

/** Parse a YYYY-MM-DD string into the matching day key, locale-free. */
export function dayKeyFromDateString(isoDate: string): DayOfWeek | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (Number.isNaN(d.getTime())) return null;
    return dayKeyFromDate(d);
}

export function timeToMinutes(hhmm: string): number {
    const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(hhmm);
    if (!m) return NaN;
    return Number(m[1]) * 60 + Number(m[2]);
}

export function minutesToTime(minutes: number): string {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function formatTime12(hhmm: string): string {
    const mins = timeToMinutes(hhmm);
    if (!Number.isFinite(mins)) return hhmm;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    const suffix = h >= 12 ? 'PM' : 'AM';
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
}

export function isAvailabilityConfigured(a: Availability | null | undefined): boolean {
    if (!a) return false;
    return DAYS_OF_WEEK.some((d) => Array.isArray(a[d]) && a[d]!.length > 0);
}

/**
 * Does the given [startMin, endMin) range lie entirely inside the union of
 * the blocks? Equivalent to: is every minute of the session covered by at
 * least one block? Adjacent/overlapping blocks are handled — a session
 * spanning a pair of back-to-back blocks (09-12 + 12-17) still counts as
 * fully inside. Any minute outside every block fails the check and the
 * caller surfaces a warning.
 */
export function rangeFitsInBlocks(
    startMin: number,
    endMin: number,
    blocks: TimeBlock[] | undefined,
): boolean {
    if (!blocks || blocks.length === 0) return false;
    const sorted = blocks
        .map((b) => [timeToMinutes(b.start), timeToMinutes(b.end)] as const)
        .filter(([s, e]) => Number.isFinite(s) && Number.isFinite(e))
        .sort((a, b) => a[0] - b[0]);
    let cursor = startMin;
    for (const [s, e] of sorted) {
        if (e <= cursor) continue;
        if (s > cursor) return false;
        if (e >= endMin) return true;
        cursor = e;
    }
    return false;
}

export type AvailabilityCheckResult =
    | { kind: 'notConfigured' }
    | { kind: 'inside' }
    | { kind: 'outside'; dayLabel: string };

/**
 * Check whether a session window on a specific date falls inside the
 * availability pattern.
 * - Returns 'notConfigured' when availability is empty/unset (no warning).
 * - Returns 'outside' when availability is configured but the session does
 *   not fit any block on that day (warn).
 * - Returns 'inside' otherwise.
 */
export function checkAvailability(
    availability: Availability | null | undefined,
    isoDate: string,
    startTime: string,
    endTime: string,
): AvailabilityCheckResult {
    if (!isAvailabilityConfigured(availability)) return { kind: 'notConfigured' };
    const dayKey = dayKeyFromDateString(isoDate);
    if (!dayKey) return { kind: 'inside' };
    const startMin = timeToMinutes(startTime);
    const endMin = timeToMinutes(endTime);
    if (!Number.isFinite(startMin) || !Number.isFinite(endMin) || endMin <= startMin) {
        return { kind: 'inside' };
    }
    const blocks = availability![dayKey];
    if (rangeFitsInBlocks(startMin, endMin, blocks)) return { kind: 'inside' };
    return { kind: 'outside', dayLabel: DAY_LABELS[dayKey] };
}
